import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Plus, Trash2, Disc3, Ticket, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Modal, inputCls } from "@/components/kit";
import { api, errMsg } from "@/lib/api";

const COLORS = ["#0B6E8F", "#C9A96A", "#34B3B0", "#0D2B3A", "#E0A458", "#5E9FB3", "#A88B5C", "#1F5F73", "#D9B77E", "#2E8C89"];

export function WheelSvg({ premi, rot = 0, spinning }) {
  const n = Math.max(premi.length, 1);
  const a = 360 / n;
  const pt = (deg, r) => [100 + r * Math.sin((deg * Math.PI) / 180), 100 - r * Math.cos((deg * Math.PI) / 180)];
  return (
    <div className="relative w-[290px] h-[290px] sm:w-[330px] sm:h-[330px] mx-auto" data-testid="wheel">
      <div className="absolute left-1/2 -top-2 -translate-x-1/2 z-10 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[26px] border-l-transparent border-r-transparent border-t-[#E0533D] drop-shadow" />
      <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-xl" style={{ transform: `rotate(${rot}deg)`, transition: spinning ? "transform 5.2s cubic-bezier(.12,.75,.12,1)" : "none" }}>
        <circle cx="100" cy="100" r="99" fill="#F2D9A6" />
        {premi.map((p, i) => {
          const [x1, y1] = pt(i * a, 94);
          const [x2, y2] = pt((i + 1) * a, 94);
          return (
            <g key={i}>
              <path d={`M100 100 L${x1} ${y1} A94 94 0 ${a > 180 ? 1 : 0} 1 ${x2} ${y2} Z`} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth="0.8" />
              <text transform={`rotate(${i * a + a / 2} 100 100)`} x="100" y="24" textAnchor="middle" fill="#fff" fontSize={n > 7 ? 6 : 7} fontWeight="600" fontFamily="'Plus Jakarta Sans', sans-serif">
                {p.length > 22 ? <><tspan x="100">{p.slice(0, p.lastIndexOf(" ", 22) > 0 ? p.lastIndexOf(" ", 22) : 22)}</tspan><tspan x="100" dy="8">{p.slice(p.lastIndexOf(" ", 22) > 0 ? p.lastIndexOf(" ", 22) + 1 : 22).slice(0, 22)}</tspan></> : p}
              </text>
            </g>
          );
        })}
        <circle cx="100" cy="100" r="14" fill="#fff" stroke="#C9A96A" strokeWidth="3" />
        <text x="100" y="104" textAnchor="middle" fontSize="11" fill="#C9A96A">★</text>
      </svg>
    </div>
  );
}

function Prize({ p, i, set, remove, tot }) {
  return (
    <div className="flex gap-2 items-center" data-testid={`wheel-prize-${i}`}>
      <input className={inputCls} value={p.nome} onChange={(e) => set({ nome: e.target.value })} placeholder="Premio (es. Prosecco di benvenuto)" data-testid={`wheel-prize-name-${i}`} />
      <input type="number" min="0" max="100" className={`${inputCls} w-20`} value={p.peso} onChange={(e) => set({ peso: e.target.value })} data-testid={`wheel-prize-weight-${i}`} />
      <span className="text-xs text-[#7B929C] w-12">{tot ? Math.round((Number(p.peso) / tot) * 100) : 0}%</span>
      <button onClick={remove} className="text-[#991B1B]" data-testid={`wheel-prize-remove-${i}`}><Trash2 size={15} /></button>
    </div>
  );
}

const GAMES = [
  { k: "gratta", label: "Gratta e vinci", desc: "card dorata da grattare col dito", Icon: Ticket },
  { k: "regali", label: "Pacchi regalo", desc: "sceglie 1 pacco su 3 che si apre", Icon: Package },
  { k: "ruota", label: "Ruota della fortuna", desc: "gira la ruota con i premi", Icon: Disc3 },
];

function GamePicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="wheel-game-picker">
      {GAMES.map(({ k, label, desc, Icon }) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`wheel-game-${k}`}
          className={`rounded-xl border-2 p-2 text-left transition-[border-color,box-shadow] ${value === k ? "border-[#0B6E8F] shadow-md bg-[#F2F8FA]" : "border-[#E3EEF1] hover:border-[#A8C6CF]"}`}>
          <Icon size={20} className={value === k ? "text-[#0B6E8F]" : "text-[#7B929C]"} />
          <div className="text-xs font-semibold mt-1">{label}</div>
          <div className="text-[10px] text-[#7B929C] leading-tight">{desc}</div>
        </button>
      ))}
    </div>
  );
}

function PrizeList({ list, setList, fallback }) {
  const tot = list.reduce((s, p) => s + Number(p.peso || 0), 0);
  const setP = (i, patch) => setList(list.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="space-y-2">
      {!list.length && fallback && <p className="text-xs text-[#7B929C] italic" data-testid="wheel-fallback-note">Nessun premio dedicato: per questo evento si usano i premi di «Tutti gli eventi».</p>}
      {list.map((p, i) => <Prize key={i} p={p} i={i} tot={tot} set={(patch) => setP(i, patch)} remove={() => setList(list.filter((_, j) => j !== i))} />)}
      {list.length < 10 && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setList([...list, { nome: "", peso: 10 }])} data-testid="wheel-prize-add"><Plus size={13} className="mr-1" /> Aggiungi premio</Button>}
    </div>
  );
}

export function WheelSettings() {
  const [open, setOpen] = useState(false);
  const [w, setW] = useState(null);
  const [tab, setTab] = useState("Tutti");
  useEffect(() => {
    api.get("/wheel").then((r) => setW(r.data));
  }, [open]);
  const save = async () => {
    try {
      setW((await api.put("/wheel", w)).data);
      toast.success(w.attiva ? "Gioco a premi attivo nel modulo richieste" : "Gioco salvato (disattivato)");
      setOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const list = w?.liste?.[tab] || [];
  const shown = list.length ? list : w?.liste?.Tutti || [];
  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="wheel-settings-button"><Gift size={15} className="mr-2" /> Gioco a premi {w?.attiva ? "• attivo" : ""}</Button>
      <Modal open={open} onOpenChange={setOpen} title="Gioco a premi" testId="wheel-settings">
        {w && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={w.attiva} onChange={(e) => setW({ ...w, attiva: e.target.checked })} data-testid="wheel-active" /> Attivo nel modulo richieste online</label>
            <GamePicker value={w.gioco} onChange={(g) => setW({ ...w, gioco: g })} />
            <p className="text-xs text-[#47606B]">Dopo l'invio della richiesta il cliente gioca una sola volta (un premio per numero di telefono). I premi dipendono dal tipo di evento scelto nel modulo. Il numero accanto a ogni premio è la sua probabilità.</p>
            <div className="flex flex-wrap gap-1.5" data-testid="wheel-event-tabs">
              {Object.keys(w.liste).map((k) => (
                <button key={k} type="button" onClick={() => setTab(k)} data-testid={`wheel-tab-${k}`}
                  className={`rounded-full px-3 py-1 text-xs border ${tab === k ? "bg-[#0B6E8F] text-white border-[#0B6E8F]" : "border-[#D5E3E8] text-[#47606B]"}`}>
                  {k === "Tutti" ? "Tutti gli eventi" : k} <span className="opacity-70">({w.liste[k].length || "="})</span>
                </button>
              ))}
            </div>
            <PrizeList list={list} fallback={tab !== "Tutti"} setList={(l) => setW({ ...w, liste: { ...w.liste, [tab]: l } })} />
            <label className="flex items-center gap-2 text-sm">Valido se firma il contratto entro <input type="number" min="1" className={`${inputCls} w-20`} value={w.giorni} onChange={(e) => setW({ ...w, giorni: e.target.value })} data-testid="wheel-days" /> giorni</label>
            {w.gioco === "ruota" && <WheelSvg premi={shown.map((p) => p.nome || "…")} />}
            <Button className="rounded-full w-full" onClick={save} data-testid="wheel-save">Salva</Button>
          </div>
        )}
      </Modal>
    </>
  );
}
